import { Button } from "../../components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "../../components/ui/sheet";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import { useEffect, useRef, useState } from "react";
import { AssetLine, POStatus } from "../../store/store";
import {
    numberToWords,
    generateSampleCSV,
    parseCSVToAssets,
} from "../../lib/currency-utils";
import { formatCurrencyINR } from "../../lib/enhanced-currency-utils";
import { validatePOData, validateCSVFormat } from "../../lib/validation";
import { validateFileType } from "../../lib/file-storage";
import {
    Download,
    Upload,
    Trash2,
    Plus,
    AlertTriangle,
    Settings,
    FileText,
    ExternalLink,
} from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { GSTSettingsDialog } from "../../components/purchaseOrder/GSTSettingsDialog";
import {
    GSTTreatment,
    determineGSTTreatment,
    extractStateFromGSTIN,
    applyGSTRules,
    calculateGSTBreakdown,
} from "../../lib/gst-rules";
import apiService from "../../services/apiService";
import { Address } from "../../types/supplier";
import { fetchCustomerAddresses, getAddressTypeLabel } from "../../lib/customer-addresses";
import { useParams } from "react-router-dom";
import { toast } from 'react-toastify';

interface ApiResponse<T> {
    data: T;
    downloaded: T;
    message?: string;
    status?: number;
}

export interface PurchaseOrder {
    id: number;
    customerId: string;
    supplierId: string;
    status: POStatus;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    deliveryAddress?: string;
    supplierAddress?: string;
    supplierGST?: string;
    approvals?: {
        underwriter: boolean;
        comments?: string;
    };
    attachments?: string[];
    lines: AssetLine[];
    purchaseOrderCode?: string;
    po: any;
}

interface POEditProps {
    po: any;
    suppliers: any;
    onCreate: () => void;
    deliveryAddress: any,
    indiaState: any[]
}

export default function EditPOSheet({
    po,
    suppliers,
    onCreate,
    deliveryAddress,
    indiaState
}: POEditProps) {

    const { customerId } = useParams();
    const [open, setOpen] = useState(false);
    const [gstSettingsOpen, setGstSettingsOpen] = useState(false);
    // const [customerId, setCustomerId] = useState("");
    const [supplierId, setSupplierId] = useState("");
    const [gstTreatment, setGstTreatment] = useState<GSTTreatment>("cgst_sgst");
    const [selectedGstinIndex, setSelectedGstinIndex] = useState(0);
    const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);


    useEffect(() => {
        if (suppliers && !supplierId) {
            setSupplierId(suppliers.id);
        }
    }, [suppliers]);

    // ✅ State for selected supplier
    const [selectedSupplier, setSelectedSupplier] = useState<any>(
        suppliers || null
    );

    // ✅ Controlled selected SPOC id (string)
    const [selectedSpocId, setSelectedSpocId] = useState<string>("");

    // ✅ Derived data states (addresses + gstins)
    const [supplierGstins, setSupplierGstins] = useState<string[]>([]);
    const [supplierAddresses, setSupplierAddresses] = useState<any[]>([]);


    // keep current selected spoc in sync when selectedSupplier changes
    useEffect(() => {
        if (selectedSupplier?.spocDetails?.length) {
            const primary = selectedSupplier.spocDetails.find(
                (s: any) => s.is_primary == true
            );
            const id = primary ? primary.id : selectedSupplier.spocDetails[0].id;
            setSelectedSpocId(String(id));
        } else {
            setSelectedSpocId("");
        }
    }, [selectedSupplier]);

    // ✅ Update supplier whenever supplierId or suppliers change
    useEffect(() => {
        if (suppliers) {

            setSelectedSupplier(suppliers);
        }
    }, [supplierId, suppliers]);

    useEffect(() => {
        if (selectedSupplier?.spocDetails?.length) {
            const primary = selectedSupplier.spocDetails.find(
                (s: any) => s.is_primary == true
            );
            const id = primary ? primary.id : selectedSupplier.spocDetails[0].id;
            setSelectedSpocId(String(id));
        } else {
            setSelectedSpocId("");
        }
    }, [selectedSupplier]);

    // helper to get currently selected spoc object
    const currentSpoc =
        selectedSupplier?.spocDetails?.find(
            (s: any) => String(s.id) === String(selectedSpocId)
        ) || selectedSupplier?.spocDetails?.[0] || null;

    // ✅ When selectedSupplier changes, recalculate GSTINs & addresses
    useEffect(() => {
        if (selectedSupplier) {
            const addresses = selectedSupplier.addresses || [];
            const gstins =
                addresses.map((addr: any) => addr.gstin).filter(Boolean) || [];

            setSupplierAddresses(addresses);
            setSupplierGstins(gstins);
            // setDeliveryAddressOptions(deliveryOptions);

            // Optional: reset address selectors
            setSelectedGstinIndex(0);
            setSelectedAddressIndex(0);
            // setSelectedDeliveryAddressIndex(0);
        }
    }, [selectedSupplier]);

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
    const [fetchedDeliveryAddress, setFetchedDeliveryAddress] = useState<any[] | null>(null);

    const deliveryAddressList =
        fetchedDeliveryAddress !== null
            ? fetchedDeliveryAddress
            : Array.isArray(deliveryAddress)
                ? deliveryAddress
                : [];

    const formatDeliveryAddressLabel = (address: any) => {
        if (!address) return "";
        const stateName =
            indiaState?.find(
                (state: any) =>
                    String(state.id) === String(address.state_id || address.state)
            )?.state_name || address.state || "";
        const typeLabel = getAddressTypeLabel(address.address_type);
        return `${typeLabel} — ${address.address_line1}, ${address.address_line2 || ""}, ${address.city}, ${stateName}, ${address.pincode}`
            .replace(/,\s*,/g, ",")
            .trim();
    };

    const formatDeliveryAddressValue = (address: any) => {
        if (!address) return "";
        const stateName =
            indiaState?.find(
                (state: any) =>
                    String(state.id) === String(address.state_id || address.state)
            )?.state_name ||
            indiaState?.find(
                (state: any) =>
                    String(state.state_name || "").toLowerCase() ===
                    String(address.state || "").toLowerCase()
            )?.state_name ||
            address.state ||
            "";
        return `${address.address_line1}, ${address.address_line2 || ""}, ${address.city}, ${stateName} - ${address.pincode}`
            .replace(/,\s*,/g, ",")
            .trim();
    };

    const normalizeAddressText = (value: string) =>
        (value || "")
            .toLowerCase()
            .replace(/\s+/g, " ")
            .replace(/\s*-\s*/g, " ")
            .replace(/,/g, " ")
            .trim();

    // Fetch all customer delivery addresses when edit sheet opens
    useEffect(() => {
        if (!open || !customerId) return;
        let cancelled = false;
        fetchCustomerAddresses(customerId)
            .then((list) => {
                if (!cancelled) setFetchedDeliveryAddress(list);
            })
            .catch(() => {
                if (!cancelled) setFetchedDeliveryAddress([]);
            });
        return () => {
            cancelled = true;
        };
    }, [open, customerId]);

    // Prefill selected delivery address from current PO value (once addresses load)
    useEffect(() => {
        if (!deliveryAddressList.length) return;
        const current = normalizeAddressText(po?.deliveryAddress || "");
        const matchIndex = current
            ? deliveryAddressList.findIndex((address: any) => {
                const label = normalizeAddressText(formatDeliveryAddressLabel(address));
                const value = normalizeAddressText(formatDeliveryAddressValue(address));
                return (
                    label === current ||
                    value === current ||
                    current.includes(normalizeAddressText(address.address_line1 || ""))
                );
            })
            : 0;
        const index = matchIndex >= 0 ? matchIndex : 0;
        setSelectedIndex(index);
        setSelectedAddress(deliveryAddressList[index]);
        // Only re-match when address list or PO address changes, not on user selection
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchedDeliveryAddress, po?.deliveryAddress]);

    const handleChange = (value: string) => {
        const index = Number(value); // convert string to number
        const address = deliveryAddressList[index];
        setSelectedIndex(index);
        setSelectedAddress(address);
    };

    useEffect(() => {
        if (!supplierAddresses[selectedGstinIndex] || !selectedAddress) return;

        const supplierStateName = supplierAddresses[selectedGstinIndex]?.state;
        const deliveryStateName = selectedAddress?.state;

        // Find state objects from india_states list
        const supplierState = indiaState.find(
            (s: any) => s.state_name === supplierStateName
        );
        const deliveryState = indiaState.find(
            (s: any) => s.state_name === deliveryStateName
        );

        if (!supplierState || !deliveryState) return;

        // Case 1: Same state
        if (supplierStateName === deliveryStateName) {
            if (deliveryState.isUnionTerritories === 1) {
                setGstTreatment("cgst_utgst");
            } else {
                setGstTreatment("cgst_sgst");
            }
        }
        // Case 2: Different states
        else {
            setGstTreatment("igst");
        }
    }, [selectedGstinIndex, selectedAddress, indiaState]);



    const [attachment, setAttachment] = useState<File | null>(null);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [lines, setLines] = useState<
        Array<{
            id?: number,
            item: string;
            description: string;
            hsn: string;
            unitPrice: number;
            qty: number;
            /** Draft string while typing decimals (e.g. "0." / "0.05") */
            qtyStr?: string;
            taxRate: number;
            assetType?: string;
            cgst?: number;
            sgst?: number;
            igst?: number;
            utgst?: number;
        }>
    >([
        {
            item: "",
            description: "",
            hsn: "",
            unitPrice: 0,
            qty: 1,
            cgst: 9,
            sgst: 9,
            taxRate: 18,
            assetType: "it-equipment",
        },
    ]);
    useEffect(() => {
        setLines(prev =>
          prev.map(l => {
            const updated = { ...l };
      
            if (gstTreatment === "cgst_sgst") {
              updated.igst = 0;
              updated.utgst = 0;
            }
      
            if (gstTreatment === "cgst_utgst") {
              updated.igst = 0;
              updated.sgst = 0;
            }
      
            if (gstTreatment === "igst") {
              updated.cgst = 0;
              updated.sgst = 0;
              updated.utgst = 0;
            }
      
            updated.taxRate =
              (updated.cgst || 0) +
              (updated.sgst || 0) +
              (updated.igst || 0) +
              (updated.utgst || 0);
      
            return updated;
          })
        );
      }, [gstTreatment]);

    useEffect(() => {
        if (!po) return;

        // map lines if available — preserve only the taxes that are actually present on each line
        if (Array.isArray(po.lines) && po.lines.length > 0) {
            setLines(
                po.lines.map((l: any) => {
                    const cg = Number(l.cgst ?? 0);
                    const sg = Number(l.sgst ?? 0);
                    const ig = Number(l.igst ?? 0);
                    const ut = Number(l.utgst ?? 0);

                    return {
                        ...l,
                        item: l.item || "",
                        description: l.description || "",
                        hsn: l.hsn || "",
                        unitPrice: l.unitPrice || 0,
                        qty: l.qty || 1,
                        // set tax fields only from values present in the PO line
                        cgst: cg > 0 ? cg : 0,
                        sgst: sg > 0 ? sg : 0,
                        igst: ig > 0 ? ig : 0,
                        utgst: ut > 0 ? ut : 0,
                        // fallback taxRate to sum of taxes if not provided
                        taxRate:
                            l.taxRate ??
                            (cg + sg + ig + ut),
                        assetType: l.assetType || "it-equipment",
                    };
                })
            );
        }

        // derive GST treatment from line taxes (prefer IGST, then UTGST, otherwise CGST/SGST)
        const hasIGST = Array.isArray(po.lines) && po.lines.some((ln: any) => Number(ln.igst || 0) > 0);
        const hasUTGST = Array.isArray(po.lines) && po.lines.some((ln: any) => Number(ln.utgst || 0) > 0);

        if (hasIGST) {
            setGstTreatment("igst");
        } else if (hasUTGST) {
            setGstTreatment("cgst_utgst");
        } else {
            setGstTreatment("cgst_sgst");
        }
    }, [po]);

    const addLine = () => {
        const breakdown = calculateGSTBreakdown(18, gstTreatment);
        setLines((l) => [
            ...l,
            {
                item: "",
                description: "",
                hsn: "",
                unitPrice: 0,
                qty: 1,
                taxRate: 18,
                assetType: "it-equipment",
                ...breakdown,
            },
        ]);
    };

    const removeLine = (index: number) => {
        if (lines.length > 1) {
            setLines((l) => l.filter((_, i) => i !== index));
        }
    };

    const downloadSampleCSV = () => {
        const csv = generateSampleCSV(gstTreatment);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "sample_assets.csv";
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!validateFileType(file, ["text/csv", "application/vnd.ms-excel"])) {
                toast("Please upload a CSV file");
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const csvContent = e.target?.result as string;
                const validation = validateCSVFormat(csvContent);

                if (!validation.valid) {
                    toast("Invalid CSV format" + validation.errors.join(", "));
                    return;
                }

                const parsedAssets = parseCSVToAssets(csvContent);
                // Apply GST rules to imported assets
                const processedAssets = parsedAssets.map((asset) =>
                    applyGSTRules(
                        asset,
                        supplierGstins[selectedGstinIndex] || "",
                        gstTreatment
                    )
                );
                setLines(processedAssets);
                toast(`${parsedAssets.length} assets imported with GST rules applied`);
            };
            reader.readAsText(file);
        }
    };


    const edit = async () => {
        // Validate PO data
        let poData = {
            id: po.id,
            customerId,
            supplierId,
            deliveryAddress:
                selectedIndex !== null && deliveryAddressList[selectedIndex]
                    ? formatDeliveryAddressValue(deliveryAddressList[selectedIndex])
                    : po.deliveryAddress,

            supplierAddress: po.supplierAddress,

            // supplierAddress:supplierAddresses[selectedGstinIndex],
            supplierGST: po.supplierGST,
            supplierSpocId: selectedSpocId,
            customerGST:
                selectedIndex !== null && deliveryAddressList[selectedIndex]
                    ? deliveryAddressList[selectedIndex]?.gstin
                    : po.customerGST,
            lines: lines.map((l) => ({
                id: l.id,
                item: l.item,
                description: l.description,
                hsn: l.hsn,
                unitPrice: l.unitPrice,
                qty: l.qty,
                assetType: l.assetType,
                cgst: l.cgst,
                sgst: l.sgst,
                igst: l.igst,
                utgst: l.utgst,
                taxRate: (l.cgst || 0) + (l.sgst || 0) + (l.igst || 0) + (l.utgst || 0),
            })),
            attachmentUrl: null,
        };

        const validation = validatePOData(poData);
        if (!validation.valid) {
            setValidationErrors(validation.errors);
            toast.error(validation.errors + "Please fix the errors before creating the PO");
            return;
        }


        if (attachment) {
            try {
                const formData = new FormData();
                // use field name expected by backend; here 'files' (adjust if API expects 'file')
                formData.append("files", attachment);
                formData.append("folder", customerId + '/po/attachment');


                const uploadResponse = await apiService.post<any>(
                    `/purchase-order/doc-upload`,
                    formData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                    }
                );

                // backend should return an array of URLs; adapt access path if different
                const urls = uploadResponse.data?.fileKeys ?? uploadResponse.data ?? [];
                poData.attachmentUrl = urls[0];

                try {
                    const response = await apiService.post<ApiResponse<any>>(
                        `/purchase-order/update-po`,
                        poData
                    );
                    if (response.status == 200) {
                        onCreate();

                        toast.success("Purchase Order Updated Successfully")

                    }
                } catch (err) {
                    toast.error('Purchase Order Updation Failed')
                }
            } catch (error) {
                toast.error("Failed to upload attachment");
                return;
            }
        } else {
            try {
                const response = await apiService.post<ApiResponse<any>>(
                    `/purchase-order/upload-po`,
                    poData
                );
                if (response.status == 200) {
                    onCreate();

                    toast.success("Purchase Order Update Successfully")

                }
            } catch (err) {
                toast.error('Purchase Order Updation Failed')
            }
        }



        setValidationErrors([]);
        setOpen(false);
    };


    return (
        <>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                    <Button variant="hero">Edit PO</Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[90vh] w-full p-0">
                    <div className="h-full flex flex-col">
                        <SheetHeader className="p-6 border-b">
                            <div className="flex items-center justify-between">
                                <SheetTitle>Purchase Order : {po.purchaseOrderCode}</SheetTitle>
                            </div>
                        </SheetHeader>

                        <ScrollArea className="flex-1 p-6">
                            <div className="space-y-6 max-w-6xl mx-auto">
                                {/* Validation Errors */}
                                {validationErrors.length > 0 && (
                                    <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                                        <div className="flex items-center gap-2 text-destructive font-medium mb-2">
                                            <AlertTriangle className="h-4 w-4" />
                                            Validation Errors
                                        </div>
                                        <ul className="text-sm text-destructive space-y-1">
                                            {validationErrors.map((error, index) => (
                                                <li key={index}>• {error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}


                                {/* Supplier Details Section - Auto-populated from selected supplier */}
                                {selectedSupplier && (
                                    <div className="space-y-4">
                                        <div className="font-semibold text-lg">
                                            Supplier Details
                                        </div>
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="space-y-2">
                                                <Label>Supplier</Label>
                                                <select
                                                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                                                    value={supplierId}
                                                    onChange={(e) => setSupplierId(e.target.value)}
                                                    disabled={true}
                                                >

                                                    <option key={suppliers.id} value={suppliers.id}>
                                                        {suppliers.company_name || "Unknown Supplier"}
                                                    </option>

                                                </select>
                                            </div>

                                            <div className="space-y-2">
                                                <Label>Contact Person</Label>
                                                <select
                                                    className="w-full bg-muted border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                                    value={selectedSpocId}
                                                    onChange={(e) => {
                                                        const selectedId = e.target.value;
                                                        setSelectedSpocId(selectedId);
                                                        const selectedSpoc = selectedSupplier?.spocDetails.find(
                                                            (s: any) => String(s.id) === String(selectedId)
                                                        );
                                                    }}
                                                >
                                                    {selectedSupplier?.spocDetails?.length ? (
                                                        selectedSupplier.spocDetails.map((spoc: any) => (
                                                            <option key={spoc.id} value={String(spoc.id)}>
                                                                {spoc.name}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        <option value="">—</option>
                                                    )}
                                                </select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Mobile Number</Label>
                                                <Input
                                                    value={currentSpoc?.mobile || "—"}
                                                    disabled
                                                    className="bg-muted"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Email</Label>
                                                <Input
                                                    value={currentSpoc?.email || "—"}
                                                    disabled
                                                    className="bg-muted"
                                                />
                                            </div>

                                            {/* GSTIN Dropdown for multiple GST numbers */}
                                            <div className="space-y-2">
                                                <Label>GSTIN</Label>

                                                <Input
                                                    value={po.supplierGST || "—"}
                                                    disabled
                                                    className="bg-muted"
                                                />
                                            </div>

                                            {/* Address Dropdown for multiple addresses */}
                                            <div className="space-y-2">
                                                <Label>Supplier Address</Label>

                                                <Textarea
                                                    value={
                                                        po.supplierAddress
                                                            ? po.supplierAddress
                                                            : "—"
                                                    }
                                                    disabled
                                                    className="bg-muted min-h-[60px]"
                                                />
                                            </div>
                                        </div>

                                        {/* Delivery Address Dropdown */}
                                        <div className="space-y-2">
                                            <Label>Delivery Address *</Label>
                                            <Select
                                                value={selectedIndex !== null ? selectedIndex.toString() : ""}
                                                onValueChange={handleChange}
                                            >
                                                <SelectTrigger className="text-sm break-words whitespace-normal min-h-[2.5rem]">
                                                    <SelectValue placeholder="Select address" />
                                                </SelectTrigger>
                                                <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                                                    {deliveryAddressList?.length > 0 ? (
                                                        deliveryAddressList.map((address: any, index: number) => (
                                                            <SelectItem
                                                                key={address.id ?? index}
                                                                value={index.toString()}
                                                                className="text-sm break-words whitespace-normal py-2"
                                                            >
                                                                <span className="text-sm break-words whitespace-normal block">
                                                                    {formatDeliveryAddressLabel(address)}
                                                                </span>
                                                            </SelectItem>
                                                        ))
                                                    ) : po?.deliveryAddress ? (
                                                        <SelectItem value="current">
                                                            {po.deliveryAddress}
                                                        </SelectItem>
                                                    ) : null}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                )}

                                <Separator />

                                {/* Asset Details Section */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="font-semibold text-lg">Asset Details</div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={downloadSampleCSV}
                                            >
                                                <Download className="h-4 w-4 mr-2" />
                                                Download Sample CSV
                                            </Button>
                                            <Dialog>
                                                <DialogTrigger asChild>
                                                    <Button variant="outline" size="sm">
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        Bulk Upload CSV
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent>
                                                    <DialogHeader>
                                                        <DialogTitle>Upload Asset CSV</DialogTitle>
                                                    </DialogHeader>
                                                    <div className="space-y-4">
                                                        <div className="text-sm text-muted-foreground">
                                                            Upload a CSV file with asset details. Make sure
                                                            your CSV follows the sample format.
                                                        </div>
                                                        <Input
                                                            type="file"
                                                            accept=".csv"
                                                            onChange={handleCSVUpload}
                                                        />
                                                    </div>
                                                </DialogContent>
                                            </Dialog>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setGstSettingsOpen(true)}
                                                className="group"
                                            >
                                                <Settings className="h-4 w-4 mr-2" />
                                                GST Settings
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Asset Headers */}
                                    <div className="grid grid-cols-12 gap-3 text-sm font-medium text-muted-foreground border-b pb-2">
                                        <div className="col-span-3">Item Description *</div>
                                        <div className="col-span-2">HSN Code</div>
                                        <div className="col-span-2">Base Price (₹) *</div>
                                        <div className="col-span-1">Qty *</div>
                                        {gstTreatment === "igst" ? (
                                            <div className="col-span-2">IGST %</div>
                                        ) : gstTreatment === "cgst_utgst" ? (
                                            <>
                                                <div className="col-span-1">CGST %</div>
                                                <div className="col-span-1">UTGST %</div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="col-span-1">CGST %</div>
                                                <div className="col-span-1">SGST %</div>
                                            </>
                                        )}
                                        <div className="col-span-1 text-right">Total (₹)</div>
                                        <div className="col-span-1 text-center">Action</div>
                                    </div>

                                    {/* Asset Rows */}
                                    {lines.map((l, idx) => {
                                        const taxAmount =
                                            gstTreatment === "igst"
                                                ? l.igst || 0
                                                : (l.cgst || 0) + (l.sgst || 0) + (l.utgst || 0);
                                        const total = l.unitPrice * l.qty * (1 + taxAmount / 100);
                                        const amountInWords =
                                            l.unitPrice > 0 ? numberToWords(l.unitPrice) : "";

                                        return (
                                            <div
                                                key={idx}
                                                className="grid grid-cols-12 gap-3 py-3 border-b text-sm items-end"
                                            >
                                                {/* Item Name Input */}
                                                <div className="col-span-3 space-y-2">
                                                    <Input
                                                        placeholder="Item name *"
                                                        value={l.item}
                                                        onChange={(e) =>
                                                            setLines((prev) =>
                                                                prev.map((line, i) =>
                                                                    i === idx
                                                                        ? { ...line, item: e.target.value }
                                                                        : line
                                                                )
                                                            )
                                                        }
                                                        className="font-medium"
                                                    />
                                                    <Textarea
                                                        placeholder="Item description"
                                                        value={l.description}
                                                        onChange={(e) =>
                                                            setLines((prev) =>
                                                                prev.map((line, i) =>
                                                                    i === idx
                                                                        ? { ...line, description: e.target.value }
                                                                        : line
                                                                )
                                                            )
                                                        }
                                                        className="min-h-[60px] resize-none text-sm"
                                                    />
                                                    {amountInWords && (
                                                        <div className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">
                                                            <strong>Amount in words:</strong> {amountInWords}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="col-span-2">
                                                    <Input
                                                        placeholder="HSN Code"
                                                        value={l.hsn}
                                                        onChange={(e) =>
                                                            setLines((prev) =>
                                                                prev.map((line, i) =>
                                                                    i === idx
                                                                        ? { ...line, hsn: e.target.value }
                                                                        : line
                                                                )
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <Input
                                                        type="number"
                                                        placeholder="0"
                                                        value={l.unitPrice || ""}
                                                        onChange={(e) =>
                                                            setLines((prev) =>
                                                                prev.map((line, i) =>
                                                                    i === idx
                                                                        ? {
                                                                            ...line,
                                                                            unitPrice:
                                                                                parseFloat(e.target.value) || 0,
                                                                        }
                                                                        : line
                                                                )
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="col-span-1">
                                                    <Input
                                                        type="text"
                                                        inputMode="decimal"
                                                        placeholder="1"
                                                        value={
                                                            l.qtyStr !== undefined
                                                                ? l.qtyStr
                                                                : l.qty === 0
                                                                  ? ""
                                                                  : String(l.qty)
                                                        }
                                                        onChange={(e) => {
                                                            const raw = e.target.value.trim();
                                                            // Allow empty / intermediate decimals like ".", "0.", "0.05"
                                                            if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;

                                                            const parsed = parseFloat(raw);
                                                            setLines((prev) =>
                                                                prev.map((line, i) =>
                                                                    i === idx
                                                                        ? {
                                                                            ...line,
                                                                            qtyStr: raw,
                                                                            qty:
                                                                                raw === "" ||
                                                                                raw === "." ||
                                                                                Number.isNaN(parsed)
                                                                                    ? 0
                                                                                    : parsed,
                                                                        }
                                                                        : line
                                                                )
                                                            );
                                                        }}
                                                        onBlur={() => {
                                                            setLines((prev) =>
                                                                prev.map((line, i) =>
                                                                    i === idx
                                                                        ? {
                                                                            ...line,
                                                                            qtyStr: undefined,
                                                                            qty: Number(line.qty) || 0,
                                                                        }
                                                                        : line
                                                                )
                                                            );
                                                        }}
                                                    />
                                                </div>

                                                {/* Dynamic Tax Fields based on GST Treatment */}
                                                {gstTreatment === "igst" ? (
                                                    <div className="col-span-2">
                                                        <Input
                                                            type="number"
                                                            placeholder="18"
                                                            value={l.igst || ""}
                                                            onChange={(e) =>
                                                                setLines((prev) =>
                                                                    prev.map((line, i) =>
                                                                        i === idx
                                                                            ? {
                                                                                ...line,
                                                                                igst: parseFloat(e.target.value) || 0,
                                                                                cgst: 0,
                                                                                sgst: 0,
                                                                                utgst: 0,
                                                                            }
                                                                            : line
                                                                    )
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                ) : gstTreatment === "cgst_utgst" ? (
                                                    <>
                                                        <div className="col-span-1">
                                                            <Input
                                                                type="number"
                                                                placeholder="9"
                                                                value={l.cgst || ""}
                                                                onChange={(e) =>
                                                                    setLines((prev) =>
                                                                        prev.map((line, i) =>
                                                                            i === idx
                                                                                ? {
                                                                                    ...line,
                                                                                    cgst:
                                                                                        parseFloat(e.target.value) || 0,
                                                                                    igst: 0,
                                                                                    sgst: 0,
                                                                                }
                                                                                : line
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                        <div className="col-span-1">
                                                            <Input
                                                                type="number"
                                                                placeholder="9"
                                                                value={l.utgst || ""}
                                                                onChange={(e) =>
                                                                    setLines((prev) =>
                                                                        prev.map((line, i) =>
                                                                            i === idx
                                                                                ? {
                                                                                    ...line,
                                                                                    utgst:
                                                                                        parseFloat(e.target.value) || 0,
                                                                                    igst: 0,
                                                                                    sgst: 0,
                                                                                }
                                                                                : line
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="col-span-1">
                                                            <Input
                                                                type="number"
                                                                placeholder="9"
                                                                value={l.cgst || ""}
                                                                onChange={(e) =>
                                                                    setLines((prev) =>
                                                                        prev.map((line, i) =>
                                                                            i === idx
                                                                                ? {
                                                                                    ...line,
                                                                                    cgst:
                                                                                        parseFloat(e.target.value) || 0,
                                                                                    igst: 0,
                                                                                    utgst: 0,
                                                                                }
                                                                                : line
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                        <div className="col-span-1">
                                                            <Input
                                                                type="number"
                                                                placeholder="9"
                                                                value={l.sgst || ""}
                                                                onChange={(e) =>
                                                                    setLines((prev) =>
                                                                        prev.map((line, i) =>
                                                                            i === idx
                                                                                ? {
                                                                                    ...line,
                                                                                    sgst:
                                                                                        parseFloat(e.target.value) || 0,
                                                                                    igst: 0,
                                                                                    utgst: 0,
                                                                                }
                                                                                : line
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                )}

                                                <div className="col-span-1 text-right font-medium">
                                                    {formatCurrencyINR(total)}
                                                </div>
                                                <div className="col-span-1 text-center">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeLine(idx)}
                                                        disabled={lines.length === 1}
                                                    >
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    <Button variant="secondary" size="sm" onClick={addLine}>
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add Another Asset
                                    </Button>
                                </div>

                                {/* Enhanced Order Summary */}
                                <div className="space-y-4">
                                    <Separator />
                                    <div className="bg-gradient-to-r from-[hsl(var(--color-admin-primary))/0.05] to-[hsl(var(--color-admin-secondary))/0.05] p-6 rounded-lg border border-[hsl(var(--color-admin-primary))/0.2] space-y-4">
                                        <h3 className="font-semibold text-lg text-foreground">
                                            Order Summary
                                        </h3>
                                        <div className="space-y-3">
                                            {/* Sub Total */}
                                            <div className="flex justify-between text-sm">
                                                <span className="text-muted-foreground">
                                                    Sub Total:
                                                </span>
                                                <span className="font-medium">
                                                    {formatCurrencyINR(
                                                        lines.reduce(
                                                            (sum, l) => sum + l.unitPrice * l.qty,
                                                            0
                                                        )
                                                    )}
                                                </span>
                                            </div>

                                            {/* GST Breakdown */}
                                            {(() => {
                                                // helper to build map percentage -> amount for a tax key
                                                const buildMap = (key: "cgst" | "sgst" | "igst" | "utgst") =>
                                                    lines.reduce((map: Record<number, number>, l) => {
                                                        const pct = Number(l[key] || 0);
                                                        if (pct <= 0) return map;
                                                        const amt = (l.unitPrice * l.qty * (l[key] || 0)) / 100;
                                                        map[pct] = (map[pct] || 0) + amt;
                                                        return map;
                                                    }, {});

                                                if (gstTreatment === "igst") {
                                                    const igstMap = buildMap("igst");
                                                    return (
                                                        <>
                                                            {Object.keys(igstMap).map((k) => (
                                                                <div key={k} className="flex justify-between text-sm">
                                                                    <span className="text-muted-foreground">IGST ({k}%) :</span>
                                                                    <span className="font-medium">{formatCurrencyINR(igstMap[Number(k)])}</span>
                                                                </div>
                                                            ))}
                                                        </>
                                                    );
                                                }

                                                if (gstTreatment === "cgst_utgst") {
                                                    // group by pair cgst_utgst
                                                    const pairMap = lines.reduce((map: Record<string, number>, l) => {
                                                        const cg = Number(l.cgst || 0);
                                                        const ut = Number(l.utgst || 0);
                                                        if (cg <= 0 && ut <= 0) return map;
                                                        const key = `${cg}_${ut}`;
                                                        const amt = (l.unitPrice * l.qty * ((cg || 0) + (ut || 0))) / 100;
                                                        map[key] = (map[key] || 0) + amt;
                                                        return map;
                                                    }, {});

                                                    return (
                                                        <>
                                                            {Object.entries(pairMap).map(([pair, amt]) => {
                                                                const [cg, ut] = pair.split("_");
                                                                if (cg === ut && Number(cg) > 0) {
                                                                    return (
                                                                        <div key={pair} className="flex justify-between text-sm">
                                                                            <span className="text-muted-foreground">CGST/UTGST ({cg}%) :</span>
                                                                            <span className="font-medium">{formatCurrencyINR(amt)}</span>
                                                                        </div>
                                                                    );
                                                                }
                                                                return (
                                                                    <div key={pair} className="space-y-1">
                                                                        <div className="flex justify-between text-sm">
                                                                            <span className="text-muted-foreground">CGST ({cg}%) :</span>
                                                                            <span className="font-medium">{formatCurrencyINR((amt * Number(cg || 0)) / (Number(cg || 0) + Number(ut || 0)) || 0)}</span>
                                                                        </div>
                                                                        <div className="flex justify-between text-sm">
                                                                            <span className="text-muted-foreground">UTGST ({ut}%) :</span>
                                                                            <span className="font-medium">{formatCurrencyINR((amt * Number(ut || 0)) / (Number(cg || 0) + Number(ut || 0)) || 0)}</span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </>
                                                    );
                                                }

                                                // default: cgst + sgst treatment
                                                const cgstMap = buildMap("cgst");
                                                const sgstMap = buildMap("sgst");

                                                // if both have exactly one identical percentage, show combined line(s)
                                                const cgKeys = Object.keys(cgstMap).map(Number).sort();
                                                const sgKeys = Object.keys(sgstMap).map(Number).sort();
                                                if (cgKeys.length === 1 && sgKeys.length === 1 && cgKeys[0] === sgKeys[0]) {
                                                    const pct = cgKeys[0];
                                                    const total = (cgstMap[pct] || 0) + (sgstMap[pct] || 0);
                                                    return (
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-muted-foreground">CGST/SGST ({pct}%) :</span>
                                                            <span className="font-medium">{formatCurrencyINR(total)}</span>
                                                        </div>
                                                    );
                                                }

                                                // otherwise list each percentage line separately (CGST then SGST)
                                                return (
                                                    <>
                                                        {Object.keys(cgstMap).map((k) => (
                                                            <div key={`cg_${k}`} className="flex justify-between text-sm">
                                                                <span className="text-muted-foreground">CGST ({k}%) :</span>
                                                                <span className="font-medium">{formatCurrencyINR(cgstMap[Number(k)])}</span>
                                                            </div>
                                                        ))}
                                                        {Object.keys(sgstMap).map((k) => (
                                                            <div key={`sg_${k}`} className="flex justify-between text-sm">
                                                                <span className="text-muted-foreground">SGST ({k}%) :</span>
                                                                <span className="font-medium">{formatCurrencyINR(sgstMap[Number(k)])}</span>
                                                            </div>
                                                        ))}
                                                    </>
                                                );
                                            })()}

                                            <Separator />
                                            <div className="flex justify-between text-lg font-bold text-[hsl(var(--color-admin-primary))]">
                                                <span>Total:</span>
                                                <span>
                                                    {formatCurrencyINR(
                                                        lines.reduce((sum, l) => {
                                                            const taxRate =
                                                                (l.cgst || 0) +
                                                                (l.sgst || 0) +
                                                                (l.igst || 0) +
                                                                (l.utgst || 0);
                                                            return (
                                                                sum + l.unitPrice * l.qty * (1 + taxRate / 100)
                                                            );
                                                        }, 0)
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Attachment Section */}
                                <div className="space-y-2">
                                    <Label>Upload Attachment</Label>

                                    {po.attachmentUrl ? (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
                                                <FileText className="h-5 w-5 text-gray-600" />
                                                <span className="text-sm flex-1">Current attachment</span>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => window.open(po.attachmentUrl, '_blank')}
                                                >
                                                    <ExternalLink className="h-4 w-4 mr-1" />
                                                    Open
                                                </Button>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <Input
                                                    type="file"
                                                    accept="application/pdf"
                                                    onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                                                    className="flex-1"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setAttachment(null)}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <Input
                                            type="file"
                                            accept="application/pdf"
                                            onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                                        />
                                    )}
                                </div>
                            </div>
                        </ScrollArea>

                        <SheetFooter className="p-6 border-t">
                            <Button variant="secondary" onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={edit} variant="hero">
                                Update Purchase Order
                            </Button>
                        </SheetFooter>
                    </div>
                </SheetContent>
            </Sheet>

            <GSTSettingsDialog
                isOpen={gstSettingsOpen}
                onClose={() => setGstSettingsOpen(false)}
                currentTreatment={gstTreatment}
                onTreatmentChange={(treatment) => {
                    setGstTreatment(treatment);
                    // Apply treatment to existing lines
                    setLines((prevLines) =>
                        prevLines.map((line) =>
                            applyGSTRules(
                                line,
                                supplierGstins[selectedGstinIndex] || "",
                                treatment
                            )
                        )
                    );
                }}
                supplierGSTIN={supplierGstins[selectedGstinIndex] || ""}
                autoDetectedTreatment={
                    supplierGstins[selectedGstinIndex] &&
                        extractStateFromGSTIN(supplierGstins[selectedGstinIndex])
                        ? determineGSTTreatment(
                            extractStateFromGSTIN(supplierGstins[selectedGstinIndex])!
                        )
                        : undefined
                }

            />
        </>
    );
}
